import type { FoldedDoors } from "../document/doors.ts";
import { COLLISION_CELLS_PER_TILE, type JsonValue, type MapDocument, type MapPlace, type MapShare, type WorldDocument } from "../document/types.ts";
import { sortedRecord } from "../document/validate.ts";
import type { JsonSchema } from "../editor/schema.ts";
import type { CompileContext } from "./context.ts";

/**
 * Doors in the compile (ADR-122 decision 2.3): the check that every field a door holds is one the game registers,
 * one server chunk's cells of the map's folded doors with the chunk's own table and each door's fields at their
 * defaults, and the door index the version keeps as `server/doors.json` for the gateway: every map's place, its
 * host resolved to a tile, its sharing, its capacity and its arrival points.
 */

/**
 * The most doors one chunk's table holds: what a cell's byte indexes.
 */
const CHUNK_DOORS_MAX = 255;

/**
 * A door as a server chunk carries it: its id, where it leads and its fields, each the game registers present.
 */
export interface ChunkDoor {
  readonly id: string;
  readonly map: string;
  readonly arrival: string;
  readonly fields: { readonly [key: string]: JsonValue };
}

/**
 * One server chunk's doors: `(chunkSize * COLLISION_CELLS_PER_TILE)^2` bytes, each 0 or a 1-based index into
 * `doors`, in the order the chunk first meets them.
 */
export interface ChunkDoors {
  readonly doors: readonly ChunkDoor[];
  readonly cells: Uint8Array;
}

/**
 * An arrival point as the door index lists it: its id, its tile position and its fields.
 */
export interface IndexArrival {
  readonly id: string;
  readonly x: number;
  readonly y: number;
  readonly fields: { readonly [key: string]: JsonValue };
}

/**
 * One map of the door index. `host` is the room's host arrival resolved to its tile on the host map, absent on a
 * map with no host and on a draft room whose host is broken; `share` is always written.
 */
export interface IndexMap {
  readonly id: string;
  readonly place?: MapPlace;
  readonly host?: { readonly map: string; readonly arrival: string; readonly x: number; readonly y: number };
  readonly share: MapShare;
  readonly capacity?: number;
  readonly arrivals: readonly IndexArrival[];
}

/**
 * The door index of a version (ADR-122 decision 2.3): every map of the world, in the world's order.
 */
export interface DoorIndex {
  readonly maps: readonly IndexMap[];
}

/**
 * Refuses a door holding a field the game does not register as a door field.
 *
 * @param map The map.
 * @param context The compile context, whose `doorFields` names the registered fields.
 * @throws Error naming the map, the layer, the door and the field.
 */
export function checkDoorFields(map: MapDocument, context: CompileContext): void {
  const known = new Set(Object.keys(context.doorFields?.properties ?? {}));
  for (const layer of map.layers) {
    if (layer.kind !== "door") {
      continue;
    }
    for (const door of layer.doors) {
      for (const field of Object.keys(door.fields)) {
        if (!known.has(field)) {
          throw new Error(`map ${map.id}, door layer ${layer.id}, door ${door.id} holds field ${field}, which the game does not register as a door field`);
        }
      }
    }
  }
}

/**
 * A door's fields as compiled: the fields it holds, and each field the game registers with a default that it
 * lacks at that default, key-sorted.
 *
 * @param fields The door's fields.
 * @param schema The game's door fields, or undefined.
 * @returns The fields.
 */
function compiledFields(fields: { readonly [key: string]: JsonValue }, schema: JsonSchema | undefined): { readonly [key: string]: JsonValue } {
  const out: Record<string, JsonValue> = { ...fields };
  for (const [name, property] of Object.entries(schema?.properties ?? {})) {
    if (!Object.hasOwn(out, name) && "default" in property && property.default !== undefined) {
      out[name] = property.default;
    }
  }
  return sortedRecord(out, "door fields");
}

/**
 * One chunk's cells of a map's folded doors, renumbered into the chunk's own table.
 *
 * @param folded The map's folded doors.
 * @param map The map, for its size.
 * @param chunk The chunk's column and row, and its size in tiles.
 * @param schema The game's door fields, whose defaults fill what a door lacks.
 * @returns The chunk's doors, or null when it holds no door cell.
 * @throws Error naming the map and the chunk when it meets more doors than a byte indexes.
 */
export function chunkDoors(folded: FoldedDoors, map: MapDocument, chunk: { readonly cx: number; readonly cy: number; readonly size: number }, schema: JsonSchema | undefined): ChunkDoors | null {
  if (folded.doors.length === 0) {
    return null;
  }
  const side = chunk.size * COLLISION_CELLS_PER_TILE;
  const gridWidth = map.width * COLLISION_CELLS_PER_TILE;
  const gridHeight = map.height * COLLISION_CELLS_PER_TILE;
  const cells = new Uint8Array(side * side);
  const remap = new Uint16Array(folded.doors.length + 1);
  const met: number[] = [];
  for (let y = 0; y < side; y += 1) {
    const gridY = chunk.cy * side + y;
    if (gridY >= gridHeight) {
      break;
    }
    for (let x = 0; x < side; x += 1) {
      const gridX = chunk.cx * side + x;
      if (gridX >= gridWidth) {
        break;
      }
      const value = folded.cells[gridY * gridWidth + gridX] as number;
      if (value === 0) {
        continue;
      }
      if (remap[value] === 0) {
        met.push(value);
        if (met.length > CHUNK_DOORS_MAX) {
          throw new Error(`map ${map.id}, chunk (${chunk.cx}, ${chunk.cy}) meets ${met.length} doors; a chunk holds at most ${CHUNK_DOORS_MAX}`);
        }
        remap[value] = met.length;
      }
      cells[y * side + x] = remap[value] as number;
    }
  }
  if (met.length === 0) {
    return null;
  }
  const doors = met.map((value) => {
    const door = folded.doors[value - 1] as FoldedDoors["doors"][number];
    return { id: door.id, map: door.to.map, arrival: door.to.arrival, fields: compiledFields(door.fields, schema) };
  });
  return { doors, cells };
}

/**
 * A map's arrival points: the entities of the game's arrival type, none when it names none.
 *
 * @param map The map.
 * @param arrivalType The game's arrival type, or undefined.
 * @returns The arrival points, in layer and placement order.
 */
function arrivalsOf(map: MapDocument, arrivalType: string | undefined): IndexArrival[] {
  if (arrivalType === undefined) {
    return [];
  }
  return map.layers.flatMap((layer) => (layer.kind === "entity" ? layer.entities.filter((entity) => entity.type === arrivalType).map((entity) => ({ id: entity.id, x: entity.x, y: entity.y, fields: sortedRecord(entity.fields, `entities.${entity.id}.fields`) })) : []));
}

/**
 * The door index of a world, or null when no map holds a door layer, so a world without doors writes the files it
 * wrote before.
 *
 * @param world The world.
 * @param context The compile context, for the arrival type.
 * @returns The index.
 */
export function doorIndexOf(world: WorldDocument, context: CompileContext): DoorIndex | null {
  if (!world.maps.some((map) => map.layers.some((layer) => layer.kind === "door"))) {
    return null;
  }
  const arrivals = new Map(world.maps.map((map) => [map.id, arrivalsOf(map, context.arrivalType)]));
  return {
    maps: world.maps.map((map) => {
      const hostPoint = map.host === undefined ? undefined : arrivals.get(map.host.map)?.find((arrival) => arrival.id === map.host?.arrival);
      return {
        id: map.id,
        ...(map.place === undefined ? {} : { place: map.place }),
        ...(map.host === undefined || hostPoint === undefined ? {} : { host: { map: map.host.map, arrival: map.host.arrival, x: hostPoint.x, y: hostPoint.y } }),
        share: map.share ?? "open",
        ...(map.capacity === undefined ? {} : { capacity: map.capacity }),
        arrivals: arrivals.get(map.id) ?? [],
      };
    }),
  };
}

/**
 * The door index as the text `server/doors.json` holds: fixed key order and a final newline, so equal indexes give
 * equal text and a version's content hash over it is stable.
 *
 * @param index The index.
 * @returns Pretty-printed JSON.
 */
export function serialiseDoorIndex(index: DoorIndex): string {
  const maps = index.maps.map((map) => ({
    id: map.id,
    ...(map.place === undefined ? {} : { place: { x: map.place.x, y: map.place.y, ...(map.place.depth === undefined ? {} : { depth: map.place.depth }) } }),
    ...(map.host === undefined ? {} : { host: { map: map.host.map, arrival: map.host.arrival, x: map.host.x, y: map.host.y } }),
    share: map.share,
    ...(map.capacity === undefined ? {} : { capacity: map.capacity }),
    arrivals: map.arrivals.map((arrival) => ({ id: arrival.id, x: arrival.x, y: arrival.y, fields: sortedRecord(arrival.fields, `arrivals.${arrival.id}.fields`) })),
  }));
  return `${JSON.stringify({ maps }, null, 2)}\n`;
}
